import { useMemo, useState } from 'react';
import type { Command } from '@integrate/contracts';
import {
  computeTargetRollup,
  fmt,
  fmtMoney,
  isPlacedInTarget,
  targetPlacementValues,
  type SeparationState,
} from '@integrate/core';
import { TargetProgress } from './TargetProgress.js';
import { LiveField } from './live-input.js';
import { Section } from './sections.js';

/**
 * The Target org phase panel, ported from `ptPanelHtml` (orgchart.html:5936).
 *
 * Every other phase decides something about the organisation that exists. This one describes the one
 * that does not yet: a ladder of tiers, a tree of units declared top-down, and — from the person and
 * group panels — who goes in which box. A unit exists on the chart before anybody is placed into it,
 * which is the whole point: you draw the shape you want and then see how far the people you have go
 * towards filling it.
 *
 * Two structural facts the panel leans on rather than re-deriving:
 *
 *   · A unit's IDENTITY IS ITS PATH. There are no ids — `['Commercial','Sales']` is the unit — so the
 *     controls here send paths, and `targetUnitOptions` is the one place that expands a declared path
 *     into every addressable prefix.
 *   · The rollup, the progress line and the chart count three DIFFERENT populations on purpose. The
 *     rollup and progress line price the design (carve-out, excluding departures); the chart draws only
 *     people actually placed, and honours the active filters. Reconciling them by re-aggregating here
 *     would make two of the three lie.
 */
export function TargetPanel({
  state,
  readOnly,
  scenarioName,
  onDispatch,
  onNext,
  onOpenFullWidth,
}: {
  state: SeparationState;
  readOnly: boolean;
  /** Placements are per-scenario, so every count on this panel is attributed to one. */
  scenarioName?: string;
  onDispatch(command: Command | readonly Command[]): Promise<boolean>;
  onNext(): void;
  /**
   * Opens this panel full width, when there is somewhere for it to go.
   *
   * Passed by the side panel and NOT by the workbench, which is how one component serves both: the
   * button appears where the 320px column is the constraint, and does not appear once the constraint
   * is gone. Absent rather than disabled, because a control that offers to widen a surface already
   * as wide as the window is not offering anything.
   */
  onOpenFullWidth?(): void;
}): JSX.Element {
  const [newTop, setNewTop] = useState('');
  const [confirmRemoveLevel, setConfirmRemoveLevel] = useState(false);

  const levels = state.targetLevels ?? [];
  const units = state.targetUnits ?? [];
  const rollup = useMemo(() => computeTargetRollup(state), [state]);
  const topLevel = useMemo(
    () => new Set(units.map((u) => u[0]).filter((v): v is string => v != null)),
    [units],
  );

  /*
    How many people a level removal would take with it.

    Named before it happens, the way the Perimeter panel names its collateral: removing the last tier
    deletes every placement value at that tier in EVERY scenario, and the command says so honestly by
    refusing to offer an inverse. Somebody about to lose forty placements should be told forty.
  */
  const deepestPlaced = useMemo(
    () =>
      [...state.employees, ...state.design.buildUps].filter(
        (p) => targetPlacementValues(state, p).length >= levels.length,
      ).length,
    [state, levels.length],
  );

  const setLevels = (next: { key: string; label: string }[]): void => {
    void onDispatch({ type: 'setTargetLevels', levels: next });
  };

  const addTop = (): void => {
    const name = newTop.trim();
    if (!name) return;
    setNewTop('');
    void onDispatch({ type: 'addTargetUnit', path: [name] });
  };

  return (
    <>
      <Section
        testId="target-intro"
        title={
          <>
            Target org design{' '}
            <span className="pill">
              {fmt(units.length)} unit{units.length === 1 ? '' : 's'}
            </span>
          </>
        }
      >
        <p className="muted">
          The organisation you are designing, drawn top-down. Declare the tiers, then the units, then
          place carve-out people into them from the chart — select a unit for its sub-units and bulk
          placement, or a person for their own.
        </p>
        <div className="sp-row">
          <span className="k">Top-level units</span>
          <span className="v" data-testid="target-top-count">
            {fmt(topLevel.size)}
          </span>
        </div>
      </Section>

      <Section
        testId="target-levels"
        title="Design levels"
      >
        <p className="muted">
          The tiers of the blueprint, outermost first. These are independent of the real org&rsquo;s
          hierarchy — the target can be shallower, deeper or shaped differently.
        </p>
        {levels.map((level, i) => (
          <div className="field" key={level.key}>
            <label htmlFor={`target-level-${level.key}`}>Level {i + 1}</label>
            <LiveField
              id={`target-level-${level.key}`}
              data-testid={`target-level-${level.key}`}
              /*
                Resynced, not remounted. The comment here used to claim the key made a REFUSAL leave the
                field showing the stored label, and it never did: a refused command leaves the label
                unchanged, so the key is unchanged and nothing remounts. What the key did do was destroy
                the box mid-word when somebody else renamed the same level.
              */
              value={level.label}
              disabled={readOnly}
              onCommit={(text, el) => {
                const label = text.trim();
                if (!label || label === level.label) {
                  el.value = level.label;
                  return;
                }
                setLevels(levels.map((l, j) => (i === j ? { ...l, label } : l)));
              }}
            />
          </div>
        ))}

        {!readOnly && (
          <div className="toggle-row">
            <button
              data-testid="target-level-add"
              onClick={() =>
                setLevels([
                  ...levels,
                  { key: `tlvl${levels.length + 1}`, label: `L${levels.length + 1}` },
                ])
              }
            >
              + Add a level
            </button>
            {/*
              Only the LAST level, and only when one would remain. The keys are positional, so removing
              a level from the middle would move every value below it up a tier — the command refuses
              that, and offering the button would be offering something that cannot work. The original
              puts its ✕ on the last level for the same reason (`:5947`).
            */}
            {levels.length > 1 && (
              <button
                className="danger"
                data-testid="target-level-remove"
                onClick={() => setConfirmRemoveLevel(true)}
              >
                Remove the last level
              </button>
            )}
          </div>
        )}

        {confirmRemoveLevel && (
          <div className="sp-row" data-testid="target-level-confirm">
            <span className="k">
              Removing &ldquo;{levels[levels.length - 1]?.label}&rdquo; truncates every unit path to{' '}
              {levels.length - 1} tier{levels.length - 1 === 1 ? '' : 's'}
              {deepestPlaced > 0 && (
                <>
                  {' '}
                  and drops {fmt(deepestPlaced)} placement{deepestPlaced === 1 ? '' : 's'} at that tier,
                  in every scenario
                </>
              )}
              . This cannot be undone.
            </span>
            <span className="v">
              <button onClick={() => setConfirmRemoveLevel(false)}>Cancel</button>{' '}
              <button
                className="danger"
                data-testid="target-level-remove-confirm"
                onClick={() => {
                  setConfirmRemoveLevel(false);
                  setLevels(levels.slice(0, -1));
                }}
              >
                Remove it
              </button>
            </span>
          </div>
        )}
      </Section>

      {!readOnly && (
        <Section
          testId="target-add-top"
          title="Add a top-level unit"
        >
          <div className="field-row">
            <input
              data-testid="target-new-top"
              value={newTop}
              placeholder="e.g. Commercial"
              onChange={(e) => setNewTop(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addTop();
              }}
            />
            <button data-testid="target-add-top-apply" disabled={!newTop.trim()} onClick={addTop}>
              Add
            </button>
          </div>
          <p className="muted">
            Sub-units are added from a unit&rsquo;s own panel — select its box on the chart.
          </p>
        </Section>
      )}

      <TargetProgress state={state} scenarioName={scenarioName} />

      {rollup.length > 0 && (
        <Section
          testId="target-rollup"
          title={<>Unit rollup{' '}{scenarioName && <span className="pill">{scenarioName}</span>}</>}
          /*
            A sibling of the toggle, never a child — see the same note on the transfer picker. Four
            columns in a scroll box 320px wide is the reason the button is here at all.
          */
          actions={
            onOpenFullWidth && (
              <button
                data-testid="target-full-width"
                title="Read the rollup with the whole window"
                onClick={onOpenFullWidth}
              >
                Full width →
              </button>
            )
          }
        >
          <p className="muted">
            Carve-out people placed in each unit and everything below it, excluding departures. The
            chart&rsquo;s filters do not apply here — the design is the design whatever is on screen.
          </p>
          <div className="table-scroll">
            <table className="tsa-table">
              <thead>
                <tr>
                  <th>Unit</th>
                  <th className="num">Headcount</th>
                  <th className="num">FTE</th>
                  <th className="num">Cost</th>
                </tr>
              </thead>
              <tbody>
                {rollup.map((row) => (
                  <tr key={`${row.Unit}:${row.Level}`} data-testid="target-rollup-row">
                    <td>{row.Unit}</td>
                    <td className="num">{fmt(row.Headcount)}</td>
                    <td className="num">{fmt(row.FTE)}</td>
                    <td className="num">{fmtMoney(row.Cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      <button className="primary" style={{ width: '100%' }} data-testid="target-next" onClick={onNext}>
        Next: build the perimeter →
      </button>
    </>
  );
}

/** Whether anybody at all has been placed, for the empty-state copy on other panels. */
export function anyPlaced(state: SeparationState): boolean {
  return [...state.employees, ...state.design.buildUps].some((p) => isPlacedInTarget(state, p));
}
